/**
 * Owner: Masa Bayu
 * File: commerce-payment-reconcile.ts
 * Date:Time: 2026-09-15 22:46
 * Description: T3-E2 payment→order→entitlement reconciliation with idempotency.
 */
import { assertNoForbiddenFoundationKey } from './authority';
import { cancelOrderAfterCancelledPayment, confirmOrderAfterSuccessfulPayment, markOrderAfterFailedPayment, } from './commerce-order-confirm';
import { linkOrderToPaymentAttempt, prepareOrderForPaymentRetry, } from './commerce-order';
import { applyPaymentCallback, createPaymentAttempt, createPaymentIntent, markPaymentProcessing, } from './commerce-payment';
import { issueEntitlementsForConfirmedOrder, } from './commerce-entitlement-issue';
import { assertValidId } from './guards';
export function createEmptyPaymentLedger() {
    return Object.freeze({
        intentsByIdempotencyKey: new Map(),
        attemptsByIdempotencyKey: new Map(),
        seenCallbackIds: new Set(),
        entitlementBatchesByOrderId: new Map(),
        confirmedOrdersById: new Map(),
    });
}
function freezeLedger(next) {
    return Object.freeze({
        intentsByIdempotencyKey: next.intentsByIdempotencyKey,
        attemptsByIdempotencyKey: next.attemptsByIdempotencyKey,
        seenCallbackIds: next.seenCallbackIds,
        entitlementBatchesByOrderId: next.entitlementBatchesByOrderId,
        confirmedOrdersById: next.confirmedOrdersById,
    });
}
function cloneLedger(ledger) {
    return {
        intentsByIdempotencyKey: new Map(ledger.intentsByIdempotencyKey),
        attemptsByIdempotencyKey: new Map(ledger.attemptsByIdempotencyKey),
        seenCallbackIds: new Set(ledger.seenCallbackIds),
        entitlementBatchesByOrderId: new Map(ledger.entitlementBatchesByOrderId),
        confirmedOrdersById: new Map(ledger.confirmedOrdersById),
    };
}
export function beginPaymentForOrder(order, ledger, options) {
    assertNoForbiddenFoundationKey(options, 'QXRead begin payment');
    assertValidId(options.idempotencyKey, 'QXRead payment idempotencyKey');
    const existingIntent = ledger.intentsByIdempotencyKey.get(options.idempotencyKey);
    const existingAttempt = ledger.attemptsByIdempotencyKey.get(options.idempotencyKey);
    if (existingIntent !== undefined && existingAttempt !== undefined) {
        return Object.freeze({
            intent: existingIntent,
            attempt: existingAttempt,
            order: linkOrderToPaymentAttempt(order, existingAttempt.attemptId),
            ledger,
            reused: true,
        });
    }
    const intent = createPaymentIntent(order, { idempotencyKey: options.idempotencyKey });
    const attempt = markPaymentProcessing(createPaymentAttempt(intent, { idempotencyKey: options.idempotencyKey }));
    const linked = linkOrderToPaymentAttempt(order, attempt.attemptId);
    const next = cloneLedger(ledger);
    next.intentsByIdempotencyKey.set(options.idempotencyKey, intent);
    next.attemptsByIdempotencyKey.set(options.idempotencyKey, attempt);
    return Object.freeze({
        intent,
        attempt,
        order: linked,
        ledger: freezeLedger(next),
        reused: false,
    });
}
export function retryPaymentForOrder(order, failedAttempt, ledger, options) {
    if (failedAttempt.state !== 'failed') {
        throw new Error('QXRead payment retry requires a failed attempt');
    }
    if (failedAttempt.attemptId !== order.paymentAttemptId) {
        throw new Error('QXRead payment retry attempt does not match pending order');
    }
    const cleared = prepareOrderForPaymentRetry(order, failedAttempt.attemptId);
    return beginPaymentForOrder(cleared, ledger, options);
}
export function reconcilePaymentCallback(input) {
    assertValidId(input.accountId, 'QXRead reconcile accountId');
    const applied = applyPaymentCallback(input.attempt, input.callback, input.ledger.seenCallbackIds);
    const next = cloneLedger(input.ledger);
    next.seenCallbackIds = new Set(applied.nextSeenCallbackIds);
    next.attemptsByIdempotencyKey.set(applied.attempt.idempotencyKey, applied.attempt);
    if (applied.duplicate) {
        const confirmed = next.confirmedOrdersById.get(input.order.orderId);
        return Object.freeze({
            attempt: applied.attempt,
            order: confirmed ?? input.order,
            entitlementBatch: next.entitlementBatchesByOrderId.get(input.order.orderId) ?? null,
            duplicateCallback: true,
            ledger: freezeLedger(next),
        });
    }
    if (applied.attempt.state === 'succeeded') {
        const existingConfirmed = next.confirmedOrdersById.get(input.order.orderId);
        const confirmed = existingConfirmed ?? confirmOrderAfterSuccessfulPayment(input.order, applied.attempt);
        const issued = issueEntitlementsForConfirmedOrder(confirmed, applied.attempt, {
            accountId: input.accountId,
            existingBatchByOrderId: next.entitlementBatchesByOrderId,
        });
        next.confirmedOrdersById.set(confirmed.orderId, issued.order);
        next.entitlementBatchesByOrderId.set(confirmed.orderId, issued.batch);
        return Object.freeze({
            attempt: applied.attempt,
            order: issued.order,
            entitlementBatch: issued.batch,
            duplicateCallback: false,
            ledger: freezeLedger(next),
        });
    }
    if (applied.attempt.state === 'failed') {
        const marked = markOrderAfterFailedPayment(input.order, applied.attempt);
        return Object.freeze({
            attempt: applied.attempt,
            order: marked,
            entitlementBatch: null,
            duplicateCallback: false,
            ledger: freezeLedger(next),
        });
    }
    if (applied.attempt.state === 'cancelled') {
        const cancelled = cancelOrderAfterCancelledPayment(input.order, applied.attempt);
        return Object.freeze({
            attempt: applied.attempt,
            order: cancelled,
            entitlementBatch: null,
            duplicateCallback: false,
            ledger: freezeLedger(next),
        });
    }
    return Object.freeze({
        attempt: applied.attempt,
        order: linkOrderToPaymentAttempt(input.order, applied.attempt.attemptId),
        entitlementBatch: null,
        duplicateCallback: false,
        ledger: freezeLedger(next),
    });
}
//# sourceMappingURL=commerce-payment-reconcile.js.map